import { Stack, Typography } from "@mui/material";
import { DateTimeInput, Input, SelectField } from "components/input";
import {
  useGetAllBoards,
  useGetAllCustomerSites,
  useGetAllSla,
  useGetBillingContractDropDownData,
  useGetServiceTypeSubtype,
  useGetTicketAttributes,
  useGlobalStore,
} from "controller";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { UrgencyData } from "shared/data";
import { convertUTCtoTimezoneV2, dateTimeToUtcV2 } from "utilz/helpers";
import { TicketCreationType } from "../CreateTicketDrawer";

interface AdditionalTicketFormProps {
  type: TicketCreationType;
  createdFrom?: "recurring_tickets" | "ticket_drawer";
}
export const AdditionalTicketForm = ({
  type,
  createdFrom = "ticket_drawer",
}: AdditionalTicketFormProps) => {
  const { control, watch, getValues, setValue } = useFormContext();
  const watchCustomer = watch("customer");
  const watchCustomerUser = watch("user");
  const timezone = useGlobalStore((state) => state.timezone);
  const { data: slaData } = useGetAllSla({
    enabled: true,
    filter: watchCustomer?.mapping_id
      ? { customerIds: [watchCustomer.mapping_id], isActive: true }
      : undefined,
  });
  const { data: contractDropDownData } = useGetBillingContractDropDownData(
    watchCustomer?.mapping_id || "",
    "ticket",
  );
  //additional
  const { data: siteData } = useGetAllCustomerSites({
    ...(watchCustomer?.mapping_id && {
      customerId: watchCustomer?.mapping_id,
      sortColumn: "site_name",
      sortDirection: "asc",
    }),
  });
  const { data: boards } = useGetAllBoards({ serviceType: "ticket_board" });
  //   ticket type data
  const { data: ticketType } = useGetServiceTypeSubtype({
    serviceType: "ticket",
  });

  // source type data
  const { data: statusData } = useGetTicketAttributes({
    attributeName: "status",
  });

  //for ticket sub type options which get based current ticket type
  const [subtype, setSubtype] = useState();

  useEffect(() => {
    if (siteData?.result && siteData.result.length > 0) {
      if (watchCustomerUser?.mapping_id) {
        const currentSite = getValues("ticket_site_id");
        const currentSiteExistsInSitesList = siteData.result.some(
          (site) => site.mapping_id === currentSite,
        );
        if (!currentSite || !currentSiteExistsInSitesList) {
          if (watchCustomerUser?.site_id) {
            setValue("ticket_site_id", watchCustomerUser.site_id, {
              shouldDirty: true,
              shouldValidate: true,
            });
          } else {
            const hqSite = siteData.result.find(
              (item) => item?.site_name === "HQ",
            );
            if (hqSite) {
              setValue("ticket_site_id", hqSite.mapping_id, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }
          }
        }
      }
    }
  }, [siteData, watchCustomer, watchCustomerUser]);

  // Reset SLA if current value is not in the new options list
  useEffect(() => {
    const currentSla = getValues("sla");
    if (!currentSla) return;
    const options = slaData?.result || [];
    const existsInOptions = options.some(
      (item: Record<string, unknown>) => item.mapping_id === currentSla,
    );
    if (!existsInOptions) {
      setValue("sla", "", { shouldDirty: false });
    }
  }, [slaData, getValues, setValue]);

  useEffect(() => {
    // load initial data
    if (getValues("ticketType")) {
      setTicketSubtype(getValues("ticketType"));
    }
  }, []);
  useEffect(() => {
    const sub = watch((data) => {
      if (data.ticketType) {
        setTicketSubtype(data.ticketType);
      }
    });

    return () => {
      sub.unsubscribe();
    };
  }, [watch, ticketType]);

  useEffect(() => {
    if (getValues("ticketType")) {
      setTicketSubtype(getValues("ticketType"));
    }
  }, [ticketType]);

  const setTicketSubtype = (id: string) => {
    const index = ticketType?.result.findIndex(
      (item: any) => item.type_id === id,
    );
    if (index >= -1) {
      setSubtype(ticketType?.result[index]?.subtype);
      // setValue("ticketSubType", "");
    }
  };
  const blockedStatus = [
    "caf3b9ab-08a5-4014-938c-aacc021f043b",
    "953b24ef-2167-49f9-88d9-a06cca165753",
    "30f434a9-4e66-4bea-86e3-86bb945035b3",
    "da5be1f9-db9d-4c64-8e43-51b458746773",
  ];

  const filteredStatus = useMemo(
    () =>
      statusData?.filter((item: any) => {
        return !blockedStatus.includes(item.mapping_id);
      }),
    [statusData],
  );

  // const deductTimeChangeHandler = (event: any) => {
  //   const regex = /^-?\d{0,3}(\.\d{0,2})?$/;
  //   const inputValue = event.target.value;
  //   if (regex.test(inputValue) || inputValue === "") {
  //     addValuesToFields({
  //       type: "budgetedHours",
  //       value: inputValue,
  //     });
  //   }
  // };

  return (
    <Stack
      component={"form"}
      sx={{
        overflow: "scroll",
        height: "100%",
        p: "16px",
      }}
    >
      <Stack sx={{ gap: "20px" }}>
        <Typography variant="subtitle1"></Typography>
        <SelectField
          name="ticket_site_id"
          label="Site"
          options={siteData?.result}
          control={control}
          labelMappingKey="site_name"
          valueMappingKey="mapping_id"
        />
        <SelectField
          name="board"
          label="Board"
          options={boards?.result}
          control={control}
          labelMappingKey="ticket_board_name"
          valueMappingKey="ticket_board_id"
          menuItemBuilder={({ item }: any) => {
            return (
              <Typography
                sx={{
                  maxWidth: "450px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {item.ticket_board_name}
              </Typography>
            );
          }}
        />
        <SelectField
          name="status"
          label="Status"
          control={control}
          required
          options={filteredStatus || []}
          labelMappingKey="status_name"
          valueMappingKey="mapping_id"
        />
        <SelectField
          name="ticketType"
          label="Service category"
          options={ticketType?.result || []}
          control={control}
          labelMappingKey="type_name"
          valueMappingKey="type_id"
        />
        <SelectField
          name="ticketSubType"
          label="Sub-category"
          options={subtype}
          control={control}
          labelMappingKey="subtype_name"
          valueMappingKey="subtype_id"
        />
        <SelectField
          name="impact"
          label="Impact"
          options={UrgencyData}
          control={control}
          labelMappingKey="label"
          valueMappingKey="value"
          isClearable
        />
        <SelectField
          name="urgency"
          label="Urgency"
          options={UrgencyData}
          labelMappingKey="label"
          valueMappingKey="value"
          control={control}
        />
        <Input
          size="small"
          fullWidth
          name="budgetedHours"
          type="number"
          label="Budgeted hours"
          control={control}
        />
        {type !== "templateCreate" &&
          type !== "templateEdit" &&
          createdFrom !== "recurring_tickets" && (
          <Controller
            control={control}
            name={"estimatedDateAndTime"}
            render={({ field }) => (
              <DateTimeInput
                disablePast
                {...field}
                value={convertUTCtoTimezoneV2(field.value, timezone)}
                timezone={timezone}
                label="Estimated end date & time"
                sx={{
                  width: "100%",
                }}
                onChangeHandler={(value) => {
                  field.onChange(dateTimeToUtcV2(value, timezone));
                }}
                slotProps={{
                  popper: {
                    placement: "top-end",
                  },
                }}
              />
            )}
          />
        )}
        {type !== "templateCreate" && type !== "templateEdit" && (
          <SelectField
            name="contract"
            label="Contract"
            options={contractDropDownData}
            labelMappingKey="contract_name"
            keepMenuItemOriginalCasing
            valueMappingKey="mapping_id"
            control={control}
          />
        )}
        {type !== "templateCreate" && type !== "templateEdit" && (
          <SelectField
            name="sla"
            label="SLA"
            options={watchCustomer?.mapping_id ? slaData?.result : []}
            labelMappingKey="sla_name"
            valueMappingKey="mapping_id"
            control={control}
          />
        )}
      </Stack>
    </Stack>
  );
};
